import MessageBubble from "./MessageBubble";
import ChatInput from "./ChatInput";
import { Phone, Video, Eye, Trash2 } from "lucide-react";
import { useState, useEffect, useRef } from "react";
import {
  getChatHistoryApi,
  deleteChatGroupApi,
  getChatUnreadApi,
  getGroupMembersApi
} from "../../app/api";
import { getSocket } from "../../socket/socket";
import { useNotificationStore } from "../../app/notificationStore";
import { useCallStore } from "../../app/callStore";

export default function ChatWindow({
  user,
  group,
  onBack,
  currentUser,
  onInspectGroup,
  onGroupDeleted
}) {
  const [messages, setMessages] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const messagesEndRef = useRef(null);
  const [socket, setSocket] = useState(null);
  const [groupMembers, setGroupMembers] = useState([]);

  const { setCallState, activeGroupCalls, callActive } = useCallStore();
  const activeGroupCall = group ? activeGroupCalls.find(c => c.groupId === group.id) : null;

  // =========================
  // 📥 FETCH HISTORY + SOCKET
  // =========================
  useEffect(() => {
    const currentSocket = getSocket();
    setSocket(currentSocket);

    const fetchHistory = async () => {
      try {
        const res = await getChatHistoryApi({
          userId: user?.id,
          groupId: group?.id,
        });

        if (res?.success) {
          const formatted = res.messages.map((m) => ({
            ...m,
            text: m.message,
            type: m.type || "text",
            sender: m.sender_id === currentUser.id ? "me" : "them",
            senderName: m.first_name
              ? `${m.first_name} ${m.last_name || ""}`
              : "",
          }));
          setMessages(formatted);
        }
      } catch (err) {
        console.error("Failed to fetch chat history", err);
      }
    };

    fetchHistory();
    if (group?.id) {
       getGroupMembersApi(group.id).then(res => {
         if (res?.success) setGroupMembers(res.members || []);
       }).catch(() => {});
    }

    // ================= SOCKET EVENTS =================
    const handleReceive = (data) => {
      if (group && data.groupId !== group.id) return;
      if (
        user &&
        !data.groupId &&
        data.senderId !== user.id &&
        data.receiverId !== user.id
      )
        return;

      setMessages((prev) => {
        // Prevent duplicates by checking data.id (which comes from backend)
        if (data.id && prev.some(m => m.id === data.id)) return prev;
        return [
          ...prev,
          {
            ...data,
            text: data.message,
            type: data.type || "text",
            sender: data.senderId === currentUser.id ? "me" : "them",
          },
        ];
      });
    };

    const handleSent = (data) => {
      if (group && data.groupId !== group.id) return;
      if (user && !data.groupId && data.receiverId !== user.id) return;

      setMessages((prev) => {
        // Prevent duplicates
        if (data.id && prev.some(m => m.id === data.id)) return prev;
        return [
          ...prev,
          { ...data, text: data.message, type: data.type || "text", sender: "me" },
        ];
      });
    };

    const handleSeen = (data) => {
      if (user && data.seenBy === user.id) {
        setMessages((prev) =>
          prev.map((m) =>
            m.sender === "me" ? { ...m, is_seen: 1 } : m
          )
        );
      }
    };

    const handleDeleted = (data) => {
      setMessages((prev) =>
        prev.map((m) =>
          m.id === data.messageId ? { ...m, is_deleted: 1 } : m
        )
      );
    };

    if (currentSocket) {
      currentSocket.on("receive_message", handleReceive);
      currentSocket.on("message_sent", handleSent);
      currentSocket.on("message_seen", handleSeen);
      currentSocket.on("message_deleted", handleDeleted);
    }

    return () => {
      if (currentSocket) {
        currentSocket.off("receive_message", handleReceive);
        currentSocket.off("message_sent", handleSent);
        currentSocket.off("message_seen", handleSeen);
        currentSocket.off("message_deleted", handleDeleted);
      }
    };
  }, [user?.id, group?.id]);

  // =========================
  // 📍 TRACK ACTIVE CHAT (for notification suppression)
  // =========================
  useEffect(() => {
    const { setActiveChat, setActiveGroup } = useNotificationStore.getState();
    if (user) setActiveChat(user.id);
    if (group) setActiveGroup(group.id);

    return () => {
      setActiveChat(null);
      setActiveGroup(null);
    };
  }, [user?.id, group?.id]);

  // =========================
  // 👁 MARK AS SEEN
  // =========================
  useEffect(() => {
    if (!socket) return;

    if (user) {
      const hasUnseen = messages.some(
        (m) => m.sender === "them" && !m.is_seen
      );

      if (hasUnseen) {
        socket.emit("mark_seen", { senderId: user.id });

        getChatUnreadApi()
          .then((res) => {
            if (res?.success) {
              useNotificationStore
                .getState()
                .setChatUnreadCount(res.count);
            }
          })
          .catch(() => {});
      }
    } else if (group) {
      socket.emit("mark_group_seen", { groupId: group.id });
    }
  }, [messages, socket, user, group]);

  // =========================
  // 🔽 AUTO SCROLL
  // =========================
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // =========================
  // 📤 SEND MESSAGE
  // =========================
  const handleSendMessage = (text, type = "text") => {
    if (!text || !socket) return;

    const payload = {
      message: text,
      type: type,
      clientTime: new Date(),
    };

    if (group) payload.groupId = group.id;
    if (user) payload.receiverId = user.id;

    socket.emit("send_message", payload);
  };

  // =========================
  // ❌ DELETE GROUP
  // =========================
  const handleDeleteGroup = async () => {
    try {
      const res = await deleteChatGroupApi(group.id);

      if (res?.success) {
        setShowDeleteModal(false);
        onGroupDeleted();
      }
    } catch (err) {
      alert("Failed to delete group");
    }
  };

  // =========================
  // 📞 INITIATE CALL
  // =========================
  const initiateCall = (video) => {
    if (!socket) return;
    
    const targetId = user?.id || null;
    const groupId = group?.id || null;

    setCallState({
      isCalling: true,
      targetUserId: targetId,
      targetUserName: targetId ? `${user.first_name} ${user.last_name}` : group.name,
      groupId: groupId,
      isVideo: video,
    });
  };

  const isAdmin = [1, 2, 3].includes(currentUser?.role_id);
  const canManage =
    group && (isAdmin || group.created_by === currentUser?.id);

  return (
    <div className="flex flex-col h-full">

      {/* HEADER */}
      <div className="flex items-center justify-between p-4 border-b border-white/10">
        <div className="flex items-center gap-3">
          <button onClick={onBack}>←</button>

          <div className="w-8 h-8 rounded-full bg-purple-500 flex items-center justify-center font-bold">
            {group
              ? group.name.charAt(0).toUpperCase()
              : user?.first_name?.charAt(0)}
          </div>

          <p className="font-medium">
            {group
              ? group.name
              : `${user?.first_name} ${user?.last_name}`}
          </p>
        </div>

        <div className="flex items-center gap-4">
          {group && (
            <button
              onClick={onInspectGroup}
              className="p-2 rounded-full hover:bg-white/10"
            >
              <Eye size={18} />
            </button>
          )}

          {canManage && (
            <button
              onClick={() => setShowDeleteModal(true)}
              className="p-2 rounded-full hover:bg-red-500/20 text-red-400"
            >
              <Trash2 size={18} />
            </button>
          )}

          <button 
            onClick={() => initiateCall(false)}
            className="p-2 rounded-full hover:bg-white/10"
          >
            <Phone size={18} />
          </button>

          <button
            onClick={() => initiateCall(true)}
            className="p-2 rounded-full hover:bg-white/10"
          >
            <Video size={18} />
          </button>
        </div>
      </div>
      
      {/* JOIN CALL BANNER */}
      {activeGroupCall && !callActive && (
        <div className="bg-[#00a884]/20 border-b border-[#00a884]/30 p-3 flex items-center justify-between px-6 animate-in slide-in-from-top duration-300">
           <div className="flex items-center gap-3">
              <div className="w-2.5 h-2.5 bg-[#00a884] rounded-full animate-pulse shadow-[0_0_8px_rgba(0,168,132,0.6)]"></div>
              <div className="flex flex-col">
                <p className="text-xs font-bold text-[#00a884] uppercase tracking-wider">Group Call Active</p>
                <p className="text-[10px] text-white/50">{activeGroupCall.senderName} started a call</p>
              </div>
           </div>
           <button 
             onClick={() => initiateCall(activeGroupCall.isVideo)}
             className="bg-[#00a884] text-white px-5 py-1.5 rounded-lg text-xs font-black hover:bg-[#008f6f] transition-all shadow-lg active:scale-95"
           >
             JOIN CALL
           </button>
        </div>
      )}

      {/* MESSAGES */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4 hide-scrollbar">
        {(() => {
          let lastDate = null;
          return messages.map((msg, index) => {
            const msgDate = new Date(msg.created_at || msg.createdAt || Date.now());
            const dateLabel = msgDate.toDateString() === new Date().toDateString() 
              ? "Today" 
              : msgDate.toDateString() === new Date(Date.now() - 86400000).toDateString()
                ? "Yesterday"
                : msgDate.toLocaleDateString([], { day: 'numeric', month: 'short', year: 'numeric' });
            
            const showDateHeader = lastDate !== dateLabel;
            lastDate = dateLabel;

            return (
              <div key={index}>
                {showDateHeader && (
                  <div className="flex justify-center my-6">
                    <span className="bg-white/5 border border-white/10 px-4 py-1 rounded-full text-[10px] text-white/40 font-black uppercase tracking-widest shadow-sm">
                      {dateLabel}
                    </span>
                </div>
                )}
                {group && msg.sender === "them" && (
                  <span className="text-[10px] text-white/30 ml-3 font-bold uppercase tracking-tighter">
                    {msg.senderName}
                  </span>
                )}
                <MessageBubble 
                  message={msg} 
                  onDelete={(id) => {
                    setMessages(prev => prev.map(m => m.id === id ? { ...m, is_deleted: 1 } : m));
                  }} 
                />
              </div>
            );
          });
        })()}
        <div ref={messagesEndRef} />
      </div>

      {/* INPUT */}
      <ChatInput onSend={handleSendMessage} members={group?.id ? groupMembers : []} />

      {/* DELETE MODAL */}
      {showDeleteModal && (
        <div className="fixed inset-0 z-[20000] flex items-center justify-center bg-black/60">
          <div className="bg-gray-800 p-6 rounded-2xl w-[320px] text-center">
            <h4 className="text-lg font-bold mb-2">
              Delete Group
            </h4>
            <p className="text-sm mb-6">
              Are you sure you want to delete this group?
            </p>

            <div className="flex gap-3 justify-center">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="px-4 py-2 bg-white/10 rounded-lg"
              >
                Cancel
              </button>

              <button
                onClick={handleDeleteGroup}
                className="px-4 py-2 bg-red-600 rounded-lg"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 💅 STYLES TO HIDE SCROLLBARS */}
      <style>{`
        .hide-scrollbar::-webkit-scrollbar {
          display: none !important;
        }
        .hide-scrollbar {
          -ms-overflow-style: none !important;
          scrollbar-width: none !important;
        }
      `}</style>
    </div>
  );
}